import type { UiDbFieldSelectModal, SearchSubqueryModal } from '#components'

interface SearchMemory {
  q: string
  r: SearchResult<any>
}

const SSKEY_SEARCH_CACHE = 'jps-search-cache'

export interface SortOption {
  name: LangString
  index: string
  asccend?: boolean
}

interface SearchConfig {
  configSyncRoute?: boolean
  configNoLoading?: boolean
  configSilent?: boolean
  configCacheSubKey?: string
  configHistory?: boolean
  configFreeze?: boolean
  configNoCache?: boolean
  queryRewriter?: (q: SearchQuery) => SearchQuery
  afterRestoreQuery?: (isSearch: boolean) => void
  stateRewriter?: (state: SearchState<any>) => void
}

interface SearchState<T> extends SearchQuery {
  searchdef: CustomSearchDefinition
  currentQuery: SearchQuery
  result: SearchResult<T>
  fullFacets: ItemFacet[]
  searching: boolean
  scrollAfterSearch: boolean
  searchService: (q: SearchQuery) => Promise<SearchResult<T>>
  storeId: string
}

export function useSearch<T>(
  cs: CustomSearchDefinition,
  searchService: (q: SearchQuery) => Promise<SearchResult<T>>,
  config?: SearchConfig,
) {
  const route = useRoute()
  const router = useRouter()
  const localSearchStore = useLocalSearchStore()
  const { setLoading } = useLoader()
  config = setDefaultIfEmpty(config, {
    configSyncRoute: true,
    configNoLoading: false,
    configSilent: false,
    configCacheSubKey: 'default',
    configHistory: true,
    configFreeze: true,
    configNoCache: false,
  })

  const state = reactive<SearchState<T>>({
    searchdef: cs,
    keyword: [],
    query: {},
    range: {},
    geoDistance: {},
    filter: {},
    from: 0,
    size: 20,
    sort: [''],
    exists: [],
    image: [],
    text2image: null,
    subquery: [],
    permission: null,
    currentQuery: null,
    result: null,
    fullFacets: null,
    searching: false,
    scrollAfterSearch: false,
    searchService: searchService,
    storeId: undefined,
  })

  onMounted(() => state.storeId = luId() as string)

  const currentQueryString: Ref<string> = ref('')
  const queryListeners: ((q: SearchQuery) => void)[] = []
  const afterSearchListener: ((result: SearchResult<T>) => void)[] = []

  let startWatchRouteEnd = null
  let configSyncRouteEnd = null

  const startWatchRoute = async () => {
    await restoreQuery()
    if (import.meta.client) {
      startWatchRouteEnd = watch(
        () => route.query,
        q => {
          restoreQuery()
        },
      )
    }
  }

  const hit = computed((): number => {
    if (state.result) return state.result.hit
    return 0
  })

  const list = computed((): T[] => {
    if (state.result) return state.result.list as T[]
    return []
  })

  const searched = computed(() => {
    return state.result !== null
  })

  const cacheKey = computed(() => {
    return (
      SSKEY_SEARCH_CACHE +
      '_' +
      window?.location?.pathname +
      '_' +
      config.configCacheSubKey
    )
  })

  const clear = () => {
    state.result = null
  }

  const setSort = (sort: string[]) => {
    state.sort = sort
    execute()
  }

  const setSize = (pagesize: number) => {
    if (pagesize > 0) {
      state.size = pagesize
      execute()
    }
  }

  const setFrom = (from: number) => {
    if (from >= 0) {
      state.from = from
      execute()
    }
  }

  function _set(
    ssField: string,
    field: string,
    words: any[],
    doExecute = true,
  ) {
    state[ssField][field] = words
    if (doExecute) {
      state.from = 0
      execute()
    }
  }

  function _add(ssField: string, field: string, words: any[], doExecute = true) {
    if (!state.query[field]) state.query[field] = []
    let newWords = [...(state[ssField][field] || [])]
    words.forEach(word => {
      if (!newWords.includes(word)) newWords.push(word)
    })
    _set(ssField, field, newWords, doExecute)
  }

  function _remove(ssField: string, field: string, words: any[], doExecute = true) {
    if (state[ssField][field]) {
      let newWords = state[ssField][field].filter(w => !words.includes(w))
      _set(ssField, field, newWords, doExecute)
    }
  }

  const addQuery = (field: string, words: string[]) => {
    _add('query', field, words)
  }

  const setQuery = (field: string, words: string[]) => {
    _set('query', field, words)
  }

  const removeQuery = (field: string, words: string[]) => {
    _remove('query', field, words)
  }

  const addFilter = (field: string, words: string[], doExecute = true) => {
    _add('filter', field, words, doExecute)
  }

  const setFilter = (field: string, words: string[]) => {
    _set('filter', field, words)
  }

  const removeFilter = (field: string, words: string[], doExecute = true) => {
    _remove('filter', field, words, doExecute)
  }

  const setGeo = (field: string, geo: GeoDistance, execute = true) => {
    _set('geoDistance', field, [geo], execute)
  }

  const removeGeo = (field: string) => {
    if (state.geoDistance[field]) {
      delete state.geoDistance[field]
      state.from = 0
      if (isEmpty(state.geoDistance)) {
        if (state.size > 100) state.size = 20
        if (state.sort.some(s => s.startsWith('geo'))) state.sort = ['']
      }
      execute()
    }
  }

  const removeRange = (field: string, range: QueryRange[]) => {
    _remove('range', field, range)
  }

  const setRange = (field: string, range: QueryRange[]) => {
    _set('range', field, range)
  }

  const removeExist = (field: string) => {
    removeFromArray(state.exists, field)
    state.from = 0
    execute()
  }

  function setScrollAfterSearch() {
    state.scrollAfterSearch = true
  }

  function clearAll() {
    clear()
    clearQuery()
    state.currentQuery = null
  }

  function clearQuery() {
    state.keyword = []
    state.query = {}
    state.range = {}
    state.geoDistance = {}
    state.filter = {}
    state.from = 0
    state.size = 20
    state.sort = ['']
    state.exists = []
    state.image = []
    state.text2image = null
    state.subquery = []
    syncQuery()
  }

  function createDebouncedExecute(
    wait: number = 300,
    errorCallback: (r) => void = null,
  ) {
    return debounce(wait, () => {
      execute(errorCallback)
    })
  }

  async function execute(errorCallback: (r) => void = null) {
    let query = getQuery()
    if (config.configSyncRoute) {
      router.push(toSearchQueryString(query)).catch(error => { })
    } else {
      await executeSearch(query, errorCallback)
    }
  }

  if (config.configSyncRoute) {
    configSyncRouteEnd = watch(
      () => route.query,
      q => {
        restoreQuery()
      },
    )
  }

  async function executeRefresh(errorCallback: (r) => void = null) {
    if (state.currentQuery) {
      await executeSearch(state.currentQuery, errorCallback, false, true)
    }
  }

  function getQuery(): SearchQuery {
    let q: SearchQuery = {}
    if (config.stateRewriter) config.stateRewriter(state)
    q.csid = state.searchdef.id
    if (state.from != null) q.from = state.from
    if (state.size != null) q.size = state.size
    if (!isEmpty(state.sort)) q.sort = state.sort
    if (state.permission != null) q.permission = state.permission
    if (!state.text2image && !isEmpty(state.keyword))
      q.keyword = clone(state.keyword)
    if (!isEmpty(state.image)) q.image = clone(state.image)
    if (state.text2image) q.text2image = clone(state.text2image)
    if (!isEmpty(state.query)) q.query = clone(state.query)
    if (!isEmpty(state.range)) q.range = clone(state.range)
    if (!isEmpty(state.filter)) q.filter = clone(state.filter)
    if (!isEmpty(state.exists)) q.exists = clone(state.exists)
    if (!isEmpty(state.geoDistance)) q.geoDistance = clone(state.geoDistance)
    if (!isEmpty(state.subquery)) q.subquery = clone(state.subquery)
    return q
  }

  function addQueryListenr(listener: (q: SearchQuery) => void) {
    queryListeners.push(listener)
  }

  function removeQueryListenr(listener) {
    removeFromArray(queryListeners, listener)
  }

  function syncQuery() {
    queryListeners.forEach(f => {
      f(state)
    })
  }

  /**
   * 検索完了後（検索結果の代入後）に呼ばれるリスナーを追加する。
   * @param listener
   */
  function addAfterSearchListener(
    listener: (result?: SearchResult<T>) => void,
  ) {
    afterSearchListener.push(listener)
  }

  function removeAfterSearchListener(listener) {
    removeFromArray(afterSearchListener, listener)
  }

  async function restoreQuery(qu?: SearchQuery) {
    let isSearch = false
    if (config.configSyncRoute || qu) {
      let q = restoreQueryWithoutExecute(qu)
      // console.info('restored-query', q)
      if (q != null) await executeSearch(q)
      isSearch = true
    } else {
      state.result = null
    }
    if (config.afterRestoreQuery) config.afterRestoreQuery(isSearch)
  }

  function getQueryString() {
    const strs = []
    if (state.keyword) strs.push(...state.keyword)
    if (state.query)
      Object.values(state.query)
        .filter(words => words && words.length > 0)
        .forEach(words => strs.push(...words))
    if (state.filter)
      Object.values(state.filter)
        .filter(words => words && words.length > 0)
        .forEach(words => strs.push(...words))
    return strs.length > 0 ? ' ' + strs.join(' ') : ''
  }

  function restoreQueryWithoutExecute(qu?: SearchQuery): SearchQuery {
    let q = qu ? qu : fromQuery(mapToListMap(route.query))
    if (!qu && q.csid !== state.searchdef.id) return null
    // console.info('restoreQueryWithoutExecute')
    state.from = q.from || 0
    state.size = q.size || 20
    state.sort = q.sort || []
    state.keyword = q.keyword || []
    state.query = q.query || {}
    state.range = q.range || {}
    state.filter = q.filter || {}
    state.exists = q.exists || []
    state.image = q.image || []
    state.text2image = q.text2image
    state.subquery = q.subquery || []
    state.geoDistance = q.geoDistance || {}
    syncQuery()
    return q
  }

  async function appendNextSearch(size: number = 20) {
    state.from += size
    let q = getQuery()
    let qstr = toSearchQueryString(q)
    let exeQuery = config.queryRewriter ? config.queryRewriter(clone(q)) : q
    state.searching = true
    try {
      let searchResult = await searchService(exeQuery)
      state.result.from = searchResult.from
      state.result.list.push(...(searchResult.list as any))
      state.currentQuery = q
      if (!config.configNoCache)
        ssSet(cacheKey.value, { q: qstr, r: searchResult })
    } catch (e) {
      throw 'search-error'
    } finally {
      state.searching = false
    }
  }

  function setSearchResult(
    sResult: SearchResult<T>,
    byIntent: boolean = true,
  ): void {
    state.result =
      config.configFreeze && sResult ? Object.freeze(sResult) : sResult
    afterSearchListener.forEach(f => f(state.result as any))

    //自動スクロール
    if (import.meta.client) {
      if (state.scrollAfterSearch || localSearchStore.globalScrollAfterSearch) {
        setTimeout(() => {
          const e = document.getElementById(state.storeId)
          if (e) {
            // グローバルヘッダー部分のサイズを考慮して移動する
            const offset = document.getElementById('globalHeader')?.getBoundingClientRect()?.height ?? 0
            window.scrollBy({
              top: e.getBoundingClientRect().top - offset,
              behavior: 'smooth'
            })
          }
          state.scrollAfterSearch = false
          localSearchStore.globalScrollAfterSearch = false
        }, 150)
      }
    }

    //検索履歴にクエリを追加
    if (state.searchdef.id != null && sResult && sResult.hit > 0 && config.configHistory) {
      //idがあるのはアイテム検索
      localSearchStore.addSearchHistory(state.currentQuery)
    }
  }

  async function executeFacet(facet: string[]): Promise<ItemFacet[]> {
    const q = getQuery()
    let exeQuery = config.queryRewriter ? config.queryRewriter(clone(q)) : q
    exeQuery.from = 0
    exeQuery.size = 0
    exeQuery.facet = facet
    return (await searchService(exeQuery)).facets
  }

  async function executeFullFacet(facet: string[]): Promise<void> {
    state.fullFacets = await executeFacet(facet)
  }

  async function executeSearch(
    q: SearchQuery,
    errorCallback: (r) => void = null,
    withIntent: boolean = true,
    refresh: boolean = false,
  ) {
    let qstr = toSearchQueryString(q)
    if (import.meta.client && !refresh && state.result != null) {
      if (
        !config.configNoCache &&
        equalsIgnoreNull(currentQueryString.value, qstr)
      )
        return
      let cache: SearchMemory = config.configNoCache
        ? null
        : ssGet(cacheKey.value)
      if (cache != null && qstr === cache.q) {
        // console.info("search " + state.cacheKey + " cache hit");
        setSearchResult(cache.r, withIntent)
        currentQueryString.value = qstr
        state.currentQuery = q
        return
      }
    }
    state.searching = true
    if (import.meta.client && !config.configNoLoading) setLoading(true)

    let exeQuery = config.queryRewriter ? config.queryRewriter(clone(q)) : q
    try {
      let result = await searchService(exeQuery)
      currentQueryString.value = qstr
      state.currentQuery = q
      setSearchResult(result, withIntent)
      if (import.meta.client && !config.configNoCache)
        ssSet(cacheKey.value, { q: qstr, r: result })
    } catch (e) {
      setSearchResult(null)
      if (errorCallback) errorCallback(e)
    } finally {
      state.searching = false
      if (import.meta.client && !config.configNoLoading) setLoading(false)
    }
  }

  function requestKeywordFocus() {
    try {
      const target = document.getElementsByClassName('jps-keyword-input')?.item(0)
      if (target) {
        (target as HTMLElement).focus()
      }
    } catch (error) {
      console.info(error)
    }
  }

  // 追加フィールド系

  function listAdditionalFields(
    q: { [key: string]: any },
    def: CustomSearchDefinition,
    excludes: string[],
  ) {
    return Object.keys(q).filter(field => {
      if (field === 'keywords') return false
      if (excludes.includes(field)) return false
      if (def.searchFields && def.searchFields.some(sf => sf.key === field))
        return false
      return true
    })
  }

  function listAdditionalFieldsFull(excludes: string[]) {
    return [
      ...listAdditionalFields(state.query, state.searchdef, excludes),
      ...listAdditionalFields(state.filter, state.searchdef, excludes),
      ...listAdditionalFields(state.exists, state.searchdef, excludes),
      ...listAdditionalFields(state.geoDistance, state.searchdef, excludes),
      ...listAdditionalFields(state.range, state.searchdef, excludes),
    ]
  }

  function findField(field: string): any {
    for (let v of ['query', 'filter', 'exists', 'geoDistance', 'range']) {
      if (state[v] && state[v][field]) return v
    }
  }

  const additionalFields = ref<string[]>([])

  addQueryListenr(q => {
    additionalFields.value = uniqueArray([
      ...additionalFields.value,
      ...listAdditionalFieldsFull([]),
    ])
  })

  const additionalFieldDefs = computed((): CustomSearchField[] => {
    return additionalFields.value.map(af => toSearchField(af)) || []
  })

  const dataStore = useDataStore()

  function toSearchField(fieldName: string): CustomSearchField {
    const csField: CustomSearchField = {}
    csField.key = fieldName
    let fieldNames = fieldName.split('~')
    csField.name = { ja: '', en: '' }
    fieldNames.forEach(f => {
      const fieldLabel = dataStore.field(fieldNames[0])
      if (fieldLabel) {
        switch (fieldLabel.indexType) {
          case 'string':
          case 'keyword':
            csField.type = 'keyword'
            break
          case 'code':
            csField.type = 'code'
            csField.codeKey = fieldLabel.codeKey
            break
        }
        csField.name.ja += (csField.name.ja ? '/' : '') + fieldLabel.label.ja
        csField.name.en += (csField.name.en ? '/' : '') + fieldLabel.label.en
      }
    })

    return csField
  }

  async function editAdditionalField(
    modal: InstanceType<typeof UiDbFieldSelectModal>,
    target?: string,
    hook?: (field: string) => void) {
    try {
      let current = []
      if (target != null) current = target.split(/~/)
      const keyword = await modal.openAwait({
        fields: current,
        indexType: ['string', 'keyword', 'code']
      })
      if (!keyword) return
      let updated = keyword.join('~')
      if (target) {
        let searchField = findField(target)
        if (searchField) {
          delete state[searchField][target]
          execute()
        }
        updateToArray(additionalFields.value, target, updated)
      } else if (!additionalFields.value.includes(updated)) {
        additionalFields.value.push(updated)
      }
      if (hook) hook(updated)
    } catch (cancel) {}
  }
  function deleteAdditionalField(field: string, hook?: (field: string) => void) {
    removeFromArray(additionalFields.value, field)
    let searchField = findField(field)
    if (searchField) {
      delete state[searchField][field]
      execute()
    }
    if (hook) hook(field)
  }

  async function editSubQuery(
    modal: InstanceType<typeof SearchSubqueryModal>,
    target?: SubQuery) {
    try {
      const subquery = await modal.openAwait({
        sub: target,
        cs: state.searchdef
      })
      if (target) {
        removeFromArray(state.subquery, target)
      } else {
        state.subquery.push(subquery)
      }
      await execute()
    } catch (cancel) { }
  }

  function deleteSubQuery(target: SubQuery) {
    removeFromArray(state.subquery, target)
    execute()
  }

  const removeWatch = () => {
    if (startWatchRouteEnd) startWatchRouteEnd()
    if (configSyncRouteEnd) configSyncRouteEnd()
  }
  onUnmounted(() => {
    removeWatch()
  })

  return {
    state,
    config,
    searched,
    clear,
    setSort,
    setSize,
    setFrom,
    addQuery,
    setQuery,
    removeQuery,
    addFilter,
    setFilter,
    removeFilter,
    setGeo,
    removeGeo,
    removeRange,
    setRange,
    removeExist,
    setScrollAfterSearch,
    clearAll,
    clearQuery,
    createDebouncedExecute,
    execute,
    executeRefresh,
    executeFullFacet,
    executeFacet,
    requestKeywordFocus,
    getQuery,
    addQueryListenr,
    removeQueryListenr,
    syncQuery,
    addAfterSearchListener,
    removeAfterSearchListener,
    restoreQuery,
    getQueryString,
    restoreQueryWithoutExecute,
    appendNextSearch,
    startWatchRoute,
    additionalFieldDefs,
    editAdditionalField,
    deleteAdditionalField,
    editSubQuery,
    deleteSubQuery,
    removeWatch,
  }
}

export type SearchStore<T> = ReturnType<typeof useSearch<T>>
